import { Turbo } from '@hotwired/turbo-rails'
import dialogPolyfill from 'dialog-polyfill'

/**
 * Confirmations Turbo dans un <dialog> HTML plutôt que window.confirm.
 * Indispensable sur Safari iOS, qui neutralise les dialogues natifs après un
 * retour par swipe jusqu'au prochain vrai chargement de page.
 * @see https://github.com/hotwired/turbo/issues/637
 *
 * Le markup vit dans layouts/_turbo_confirm.html.erb, rendu par le layout
 * application — donc présent sur toutes les pages (gods et profile_settings
 * se terminent par un render template de ce layout).
 */
Turbo.config.forms.confirm = (message, formElement, submitter) => {
  // Dans l'app, la boîte système (bridge--confirm) remplace le <dialog>.
  const native = nativeConfirm()
  if (native) {
    return native.ask({
      title: confirmTitle(formElement, submitter),
      message,
      confirmLabel: confirmLabel(formElement, submitter),
      destructive: isDestructive(formElement),
    })
  }

  const dialog = document.getElementById('turbo-confirm')

  // Safari/iOS < 15.4 n'a pas <dialog> natif : sans polyfill showModal() lève,
  // la promesse rejette dans Turbo et l'action est abandonnée sans rien afficher.
  // Le garde rend l'appel idempotent (le polyfill installe showModal lui-même).
  if (!dialog.showModal) dialogPolyfill.registerDialog(dialog)

  dialog.querySelector('h3').textContent = confirmTitle(formElement, submitter)
  dialog.querySelector('p').innerHTML = emphasizeQuotedName(escapeHtml(message))
  dialog.returnValue = ''
  dialog.onclick = (event) => {
    if (event.target === dialog) dialog.close()
  }
  dialog.showModal()

  return new Promise((resolve) => {
    dialog.addEventListener('close', () => resolve(dialog.returnValue === 'confirm'), {
      once: true,
    })
  })
}

// Pour un link_to en data-turbo-method, Turbo fabrique un formulaire caché et
// n'y recopie que data-turbo-confirm, puis le soumet sans submitter : le titre
// posé sur le lien n'atteindrait jamais le callback. On le capte au clic.
let clickedLinkTitle = null

document.addEventListener(
  'click',
  (event) => {
    clickedLinkTitle =
      event.target.closest?.('a[data-turbo-confirm-title]')?.dataset.turboConfirmTitle || null
  },
  true
)

function confirmTitle(formElement, submitter) {
  const title =
    submitter?.dataset?.turboConfirmTitle ||
    formElement?.dataset?.turboConfirmTitle ||
    clickedLinkTitle
  clickedLinkTitle = null

  return title || 'Confirmation'
}

function nativeConfirm() {
  const supported = (document.documentElement.dataset.bridgeComponents || '').split(' ')
  if (!supported.includes('confirm')) return null

  return window.Stimulus.getControllerForElementAndIdentifier(document.body, 'bridge--confirm')
}

// Libellé du bouton qui valide : data-turbo-confirm-label sur le bouton ou le
// formulaire, sinon celui du dialogue web (« Confirmer ») — l'action derrière une
// confirmation de page varie trop pour un défaut par méthode.
function confirmLabel(formElement, submitter) {
  return (
    submitter?.dataset?.turboConfirmLabel ||
    formElement?.dataset?.turboConfirmLabel ||
    document.querySelector('#turbo-confirm button[value="confirm"]')?.textContent?.trim() ||
    ''
  )
}

// Une suppression (méthode DELETE, y compris via le champ _method de Rails)
// s'affiche en destructif : bouton rouge dans la boîte système.
function isDestructive(formElement) {
  const method = formElement?.querySelector('input[name="_method"]')?.value || formElement?.method
  return (method || '').toLowerCase() === 'delete'
}

function escapeHtml(text) {
  const div = document.createElement('div')
  div.textContent = text
  return div.innerHTML
}

// Mises en avant du message : les noms cités entre guillemets et les segments
// balisés *ainsi* dans les locales ressortent en medium.
function emphasizeQuotedName(html) {
  return html
    .replace(/«[^»]*»/g, '<span class="font-medium">$&</span>')
    .replace(/\*([^*\n]+)\*/g, '<span class="font-medium">$1</span>')
}
